// Enums de pagamento moram em src/constants/payment.ts (compartilhados com o billing
// e com os componentes de components/molecules/payment). Re-exportados aqui para não
// quebrar os imports existentes deste domínio.
export {
    PaymentType, paymentTypeLabels,
    PaymentMethod, paymentMethodLabels,
    PaymentSource, paymentSourceLabels,
    SettlementType,
    AssetType, assetTypeLabels,
} from '../../../../constants/payment';
export type { AssetDationItem } from '../../../../constants/payment';

export const TitleDebtStatus = {
    OPEN: 'OPEN',
    PARTIALLY_PAID: 'PARTIALLY_PAID',
    PAID: 'PAID',
    SUPERSEDED: 'SUPERSEDED',
    CANCELLED: 'CANCELLED',
} as const satisfies Record<string, string>;
export type TitleDebtStatus = typeof TitleDebtStatus[keyof typeof TitleDebtStatus];

// BankAccount (enum hardcoded) removido — contas de recebimento agora vêm de
// GET /billing/receiving-bank-accounts via <ReceivingBankAccountSelect> (AF-3321).

export const MovementType = {
    TOTAL_PAYMENT: 'TOTAL_PAYMENT',
    PARTIAL_PAYMENT: 'PARTIAL_PAYMENT',
    RENEGOTIATION: 'RENEGOTIATION',
    REVERSAL: 'REVERSAL',
} as const satisfies Record<string, string>;
export type MovementType = typeof MovementType[keyof typeof MovementType];

export const ReversalReason = {
    PAGAMENTO_DUPLICADO: 'Pagamento duplicado',
    VALOR_INCORRETO: 'Valor incorreto',
    OPERACAO_CANCELADA: 'Operação cancelada',
    ERRO_SISTEMA: 'Erro de sistema',
} as const satisfies Record<string, string>;
export type ReversalReason = typeof ReversalReason[keyof typeof ReversalReason];

/**
 * Paleta de cobrança, compartilhada pelas telas de Títulos, Operações e seus modais.
 *
 * A regra é uma só: **todo pagamento é verde**, e o tom diz qual pagamento. Assim o olho aprende
 * "verde = entrou dinheiro" e a variação carrega o detalhe, em vez de cada estado ter uma cor
 * arbitrária. O que NÃO é pagamento sai da família: aberto em gold, "falta faturar" em laranja
 * (pendência nossa, não do cliente), renegociado em cinza e cancelado em vermelho.
 */
export const statusColors: Record<string, 'blue' | 'green' | 'red' | 'orange' | 'gray' | 'yellow' | 'purple' | 'olive' | 'teal' | 'gold' | 'todo' | 'turquoise'> = {
    OPEN: 'gold',
    PAID: 'green',              // liquidado
    PARTIALLY_PAID: 'olive',    // pagamento parcial
    OVERPAID: 'teal',           // recebimento a maior
    PENDING: 'todo',            // falta faturar — ação nossa
    SUPERSEDED: 'gray',
    CANCELLED: 'red',
};

/** Cor do MOVIMENTO, na mesma família: liquidação verde, parcial olive, pós-liquidação teal. */
export const movementTypeColors: Record<string, 'green' | 'olive' | 'teal' | 'gray' | 'red'> = {
    TOTAL_PAYMENT: 'green',
    PARTIAL_PAYMENT: 'olive',
    OVERPAYMENT: 'teal',
    RENEGOTIATION: 'gray',
    REVERSAL: 'red',
};

export const statusLabels: Record<string, string> = {
    OPEN: 'Em aberto',
    PARTIALLY_PAID: 'Parcial',
    PAID: 'Pago',
    SUPERSEDED: 'Renegociado',
    CANCELLED: 'Cancelado',
};

export const movementTypeLabels: Record<string, string> = {
    TOTAL_PAYMENT: 'Liquidação',
    PARTIAL_PAYMENT: 'Pagamento Parcial',
    RENEGOTIATION: 'Renegociação',
    REVERSAL: 'Estorno',
    // pagamento que chegou depois de o título já estar liquidado (sink OVERPAID). Sem esta
    // entrada a tela caía no fallback e imprimia "OVERPAYMENT" cru.
    OVERPAYMENT: 'Pagamento Pós Liq.',
};

/**
 * Natureza do título. Singular para cabeçalho de coluna, plural para contagem — a mesma operação
 * chama a linha de "Parcela" ou "Recebível" conforme o `business_reference`, e chamar tudo de
 * "Parcela" confunde em cessão de recebíveis.
 */
export const businessReferenceLabels: Record<string, string> = {
    INSTALLMENT: 'Parcela',
    RECEIVABLE: 'Recebível',
    GUARANTEE: 'Garantia',
};

export const businessReferenceLabelsPlural: Record<string, string> = {
    INSTALLMENT: 'Parcelas',
    RECEIVABLE: 'Recebíveis',
    GUARANTEE: 'Garantias',
};

export const settlementTypeLabels: Record<number, string> = {
    1: 'Total',
    2: 'Parcial',
    3: 'Em Atraso',
    4: 'Antecipado',
};
